import * as React from 'react'
import { useState } from 'react'
import {
  useSaveResourcesTemplateMutation,
  useUpdateResourcesTemplateMutation,
  useDeleteResourcesTemplateMutation,
} from '../../hooks/queries/resources_templates'
import SaveTemplatePopup from './SaveTemplatePopup'
import ManageTemplatesPopup from './ManageTemplatesPopup'

type Template = {
  id: number
  name: string
  template_data?: any
}

type TemplateManagerProps = {
  formData: any
  setFormData: (data: any) => void
  currentUser: any
  updateCurrentUser: (user: any) => void
}

const sortTemplatesByName = (templates: Template[]) =>
  [...templates].sort((firstTemplate, secondTemplate) =>
    firstTemplate.name.localeCompare(secondTemplate.name, undefined, { sensitivity: 'base' })
  )

export default function TemplateManager({ formData, setFormData, currentUser, updateCurrentUser }: TemplateManagerProps) {
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [showManageModal, setShowManageModal] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)

  const templates = sortTemplatesByName(currentUser.resources_templates || [])

  const saveTemplateMutation = useSaveResourcesTemplateMutation(data => {
    const newTemplate = data.data
    const updatedUser = {
      ...currentUser,
      resources_templates: sortTemplatesByName([...templates, newTemplate]),
    }
    updateCurrentUser(updatedUser)
    setShowSaveModal(false)
  })

  const updateTemplateMutation = useUpdateResourcesTemplateMutation(data => {
    const updatedTemplate = data.data
    const updatedTemplates = sortTemplatesByName(templates.map((t: Template) => (t.id === updatedTemplate.id ? updatedTemplate : t)))
    const updatedUser = {
      ...currentUser,
      resources_templates: updatedTemplates,
    }
    updateCurrentUser(updatedUser)
  })

  const deleteTemplateMutation = useDeleteResourcesTemplateMutation(() => {
    // The template ID to delete is passed when mutation is called
  })

  const handleSaveTemplate = (templateName: string) => {
    saveTemplateMutation.mutate({
      name: templateName,
      template_data: formData,
    })
  }

  const handleSaveOrUpdateTemplate = () => {
    if (selectedTemplateId) {
      // Update existing template
      const selectedTemplate = templates.find((t: Template) => t.id === selectedTemplateId)
      if (selectedTemplate) {
        updateTemplateMutation.mutate({
          id: selectedTemplateId,
          name: selectedTemplate.name,
          template_data: formData,
        })
      }
    } else {
      // Create new template - show popup
      setShowSaveModal(true)
    }
  }

  const handleTemplateChange = (templateId: number | null) => {
    setSelectedTemplateId(templateId)

    if (!templateId) return

    const template = templates.find((t: Template) => t.id === templateId)
    if (template && template.template_data) {
      const templateData = { ...template.template_data }
      templateData.date = formData.date // Preserve current date
      setFormData(templateData)
    }
  }

  const handleUpdateTemplate = (templateId: number, newName: string) => {
    updateTemplateMutation.mutate(
      { id: templateId, name: newName },
      {
        onSuccess: data => {
          const updatedTemplate = data.data
          const updatedTemplates = sortTemplatesByName(templates.map((t: Template) => (t.id === updatedTemplate.id ? updatedTemplate : t)))
          const updatedUser = {
            ...currentUser,
            resources_templates: updatedTemplates,
          }
          updateCurrentUser(updatedUser)
        },
      }
    )
  }

  const handleDeleteTemplate = (templateId: number, templateName: string) => {
    if (confirm(`Are you sure you want to delete template "${templateName}"?`)) {
      deleteTemplateMutation.mutate(templateId, {
        onSuccess: () => {
          const updatedTemplates = sortTemplatesByName(templates.filter((t: Template) => t.id !== templateId))
          const updatedUser = {
            ...currentUser,
            resources_templates: updatedTemplates,
          }
          updateCurrentUser(updatedUser)
        },
      })
    }
  }

  return (
    <div className="template-manager mb-3">
      <div className="row">
        {/* Load Template */}
        <div className="col-12 col-sm-6 form-group d-flex align-items-center">
          <select
            className="form-control"
            value={selectedTemplateId || ''}
            onChange={e => handleTemplateChange(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Vælg skabelon...</option>
            {templates.map((template: Template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-sm-6 col-12 form-group d-flex justify-content-end">
          <div className="btn-group" role="group">
            <button 
              type="button" 
              className="btn btn-outline-primary" 
              onClick={handleSaveOrUpdateTemplate}
              disabled={saveTemplateMutation.isLoading || updateTemplateMutation.isLoading}
            >
              {saveTemplateMutation.isLoading || updateTemplateMutation.isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {selectedTemplateId ? 'Opdaterer...' : 'Gemmer...'}
                </>
              ) : (
                'Gem skabelon'
              )}
            </button>
            {templates.length > 0 && (
              <button
                type="button"
                className="btn btn-outline-primary dropdown-toggle dropdown-toggle-split"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                <span className="sr-only">Toggle Dropdown</span>
              </button>
            )}
            {templates.length > 0 && (
              <div className="dropdown-menu dropdown-menu-right">
                <button className="dropdown-item" type="button" onClick={() => setShowManageModal(true)}>
                  Administrer skabeloner
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <SaveTemplatePopup
        open={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveTemplate}
        isLoading={saveTemplateMutation.isLoading}
      />

      <ManageTemplatesPopup
        open={showManageModal}
        onClose={() => setShowManageModal(false)}
        templates={templates}
        onUpdateTemplate={handleUpdateTemplate}
        onDeleteTemplate={handleDeleteTemplate}
        isUpdating={updateTemplateMutation.isLoading}
        isDeleting={deleteTemplateMutation.isLoading}
      />
    </div>
  )
}
